import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/Spinner";
import { cn } from "@/lib/utils";
import type { CarrierData, PortalState } from "@/store/portal-store";

interface Step3Props {
  state: PortalState;
  updateState: (partial: Partial<PortalState>) => void;
  onNext: () => void;
}

const MOCK_CARRIER: CarrierData = {
  legalName: "Big Rig Logistics LLC",
  dba: "BRL Trucking",
  usdot: "1234567",
  mc: "MC-987654",
  operationType: "Interstate",
  carrierType: "Carrier",
  powerUnits: 3,
  authorityStatus: "Active",
};

export function Step3DOTLookup({ state, updateState, onNext }: Step3Props) {
  const [usdot, setUsdot] = useState("");
  const [loading, setLoading] = useState(false);
  const [carrier, setCarrier] = useState<CarrierData | null>(null);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  const isValid = /^\d{1,8}$/.test(usdot);

  const handleLookup = () => {
    setLoading(true);
    setError("");
    setNotFound(false);
    setCarrier(null);

    setTimeout(() => {
      setLoading(false);
      if (usdot === "0000000" || usdot === "00000000") {
        setNotFound(true);
      } else {
        setCarrier({ ...MOCK_CARRIER, usdot });
      }
    }, 2000);
  };

  const handleConfirm = () => {
    if (carrier) {
      updateState({ carrier });
      onNext();
    }
  };

  const handleClear = () => {
    setUsdot("");
    setCarrier(null);
    setNotFound(false);
    setError("");
  };

  return (
    <div className="flex flex-col px-6 py-6">
      <h2 className="text-xl font-bold mb-2">Enter Your USDOT Number</h2>
      <p className="text-gray-500 text-sm mb-6">
        We'll look up your carrier information from FMCSA records.
      </p>

      <div className="space-y-2 mb-4">
        <Label htmlFor="usdot">USDOT Number</Label>
        <Input
          id="usdot"
          type="text"
          inputMode="numeric"
          placeholder="e.g., 1234567"
          value={usdot}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 8);
            setUsdot(v);
            setNotFound(false);
            setCarrier(null);
          }}
          disabled={loading}
          className="h-12 text-base"
        />
        {notFound && (
          <p className="text-sm text-red-500">
            No carrier found for this USDOT number. Please check and try again.
          </p>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>

      {!carrier && (
        <Button
          className="w-full h-12 text-base mb-6"
          disabled={!isValid || loading}
          onClick={handleLookup}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" className="border-white border-t-transparent" />
              Looking up...
            </span>
          ) : (
            "Look Up"
          )}
        </Button>
      )}

      {carrier && (
        <Card className="mb-4">
          <CardContent className="space-y-3 pt-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">{carrier.legalName}</h3>
              <span
                className={cn(
                  "px-2 py-1 rounded-full text-xs font-semibold",
                  carrier.authorityStatus === "Active"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                )}
              >
                {carrier.authorityStatus}
              </span>
            </div>
            {carrier.dba && carrier.dba !== carrier.legalName && (
              <p className="text-sm text-gray-500">DBA: {carrier.dba}</p>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-400">USDOT</span>
                <p className="font-medium">{carrier.usdot}</p>
              </div>
              <div>
                <span className="text-gray-400">MC #</span>
                <p className="font-medium">{carrier.mc}</p>
              </div>
              <div>
                <span className="text-gray-400">Operation</span>
                <p className="font-medium">{carrier.operationType}</p>
              </div>
              <div>
                <span className="text-gray-400">Type</span>
                <p className="font-medium">{carrier.carrierType}</p>
              </div>
              <div>
                <span className="text-gray-400">Power Units</span>
                <p className="font-medium">{carrier.powerUnits}</p>
              </div>
            </div>

            {carrier.authorityStatus === "Active" ? (
              <Button className="w-full h-12 text-base mt-2" onClick={handleConfirm}>
                This Is My Company
              </Button>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                <p className="text-sm text-red-600">
                  Your operating authority is currently inactive. An active USDOT
                  authority is required for coverage. Contact FMCSA or your agent
                  for assistance.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {carrier && (
        <button
          type="button"
          onClick={handleClear}
          className="text-sm text-blue-500 text-center py-2"
        >
          This doesn't look right
        </button>
      )}
    </div>
  );
}
